import CopyableId from "@/components/common/CopyableId";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, PhoneCall, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { SearchableSelect } from "@/components/common/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { DIAL_CODES, PATIENT_IDENTITY_TYPES, GENDER_OPTIONS } from "@/lib/dialCodes";
import { SAUDI_REGIONS, SAUDI_CITIES } from "@/lib/saudiRegions";

const DURATION_SUGGESTIONS = [
  "Less than 1 hour", "1-2 hours", "A few hours", "Since this morning",
  "1 day", "2-3 days", "About a week", "More than a week", "Ongoing / chronic",
];


const EMPTY_NEW_PATIENT = {
  first_name: "", second_name: "", last_name: "",
  dial_code: "+966", mobile: "",
  dob: "", gender: "",
  nationality: "", identity_type: "", identity_number: "",
  region: "", city: "",
};

function identityNumberError(itype, inum) {
  const n = (inum || "").trim();
  if (!itype) return null;
  if (!n) return "Identity number is required.";
  if (itype === "National Number" && !/^1\d{9}$/.test(n)) return "National Number must start with 1 and be exactly 10 digits.";
  if (itype === "Permanent Resident" && !/^2\d{9}$/.test(n)) return "Permanent Resident must start with 2 and be exactly 10 digits.";
  if (itype === "Border Number" && !/^[34]\d{9}$/.test(n)) return "Border Number must be 10 digits starting with 3 or 4.";
  if (itype === "Passport" && !/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{5,30}$/.test(n)) return "Passport must be 5-30 characters, a mix of letters and numbers.";
  return null;
}

export default function NewHotlineSession() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=identify, 2=session
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [newPatient, setNewPatient] = useState(EMPTY_NEW_PATIENT);
  const [npErrors, setNpErrors] = useState({});
  const [npTouched, setNpTouched] = useState({});
  const [npSubmitAttempted, setNpSubmitAttempted] = useState(false);
  const [natOptions, setNatOptions] = useState([]);
  const [registering, setRegistering] = useState(false);

  const [complaintCategories, setComplaintCategories] = useState([]);
  const [chief, setChief] = useState({ category: "", description: "", severity: "mild", duration: "" });
  const [priority, setPriority] = useState("routine");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/lookups/categories", { params: { page_size: 200 } }).then(async (cats) => {
      const cat = cats.data.items.find((c) => c.code === "complaint_categories");
      if (cat) {
        const v = await api.get("/lookups/values", { params: { category_id: cat.id } });
        setComplaintCategories(v.data.items);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/hotline/patients/search", { params: { q: query } });
        setResults(res.data.items || []);
      } catch (_e) { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    api.get("/forms/lookup-options/nationality").then((r) => setNatOptions(r.data.items || [])).catch(() => {});
  }, []);

  const isSaudiNew = (newPatient.nationality || "").toLowerCase() === "saudi";

  function computeNpErrors(f) {
    const e = {};
    if (!(f.first_name || "").trim()) e.first_name = "First name is required";
    if (!(f.last_name || "").trim()) e.last_name = "Last name is required";
    if (f.mobile && !f.dial_code) e.dial_code = "Select a country code first";
    if (f.mobile && !/^\d{6,15}$/.test(f.mobile.trim())) e.mobile = "Enter a valid mobile number (digits only)";
    if (f.identity_type) {
      const ierr = identityNumberError(f.identity_type, f.identity_number);
      if (ierr) e.identity_number = ierr;
    }
    return e;
  }

  useEffect(() => {
    if (!registerOpen) return;
    const all = computeNpErrors(newPatient);
    const visible = npSubmitAttempted ? all : Object.fromEntries(Object.entries(all).filter(([k]) => npTouched[k]));
    setNpErrors((prev) => {
      const merged = { ...visible };
      if (prev.identity_number && !all.identity_number && prev.identity_number.includes("already exists")) merged.identity_number = prev.identity_number;
      return merged;
    });
    // eslint-disable-next-line
  }, [newPatient, npTouched, npSubmitAttempted, registerOpen]);

  function setNpField(name, value) {
    setNewPatient((f) => ({ ...f, [name]: value }));
    setNpTouched((t) => ({ ...t, [name]: true }));
  }
  function setNationality(v) {
    setNewPatient((f) => {
      const next = { ...f, nationality: v };
      if (v.toLowerCase() === "saudi") next.identity_type = "National Number";
      return next;
    });
    setNpTouched((t) => ({ ...t, nationality: true }));
  }
  function setRegion(v) {
    setNewPatient((f) => ({ ...f, region: v, city: "" }));
    setNpTouched((t) => ({ ...t, region: true }));
  }

  async function registerPatient() {
    setNpSubmitAttempted(true);
    const e = computeNpErrors(newPatient);
    setNpErrors(e);
    if (Object.keys(e).length > 0) { toast.error("Please fix the highlighted fields before saving."); return; }
    setRegistering(true);
    try {
      const payload = {
        first_name: newPatient.first_name.trim(),
        second_name: (newPatient.second_name || "").trim(),
        last_name: newPatient.last_name.trim(),
        dial_code: newPatient.dial_code,
        mobile: (newPatient.mobile || "").trim(),
        gender: newPatient.gender,
        dob: newPatient.dob,
        nationality: newPatient.nationality,
        identity_type: newPatient.identity_type || null,
        identity_number: (newPatient.identity_number || "").trim() || null,
        region: newPatient.region,
        city: newPatient.city,
      };
      const res = await api.post("/hotline/patients/register", payload);
      toast.success("Hotline patient registered");
      setSelectedPatient(res.data);
      setRegisterOpen(false);
      setNewPatient(EMPTY_NEW_PATIENT);
      setNpTouched({});
      setNpSubmitAttempted(false);
      setStep(2);
    } catch (err) {
      const fe = err?.response?.data?.detail?.field_errors;
      if (fe && typeof fe === "object") { setNpErrors((prev) => ({ ...prev, ...fe })); setNpSubmitAttempted(true); }
      toast.error(formatApiError(err));
    }
    finally { setRegistering(false); }
  }

  async function createSession() {
    if (!selectedPatient) { toast.error("Pick a patient first."); return; }
    setCreating(true);
    try {
      const res = await api.post("/hotline/sessions", {
        patient_id: selectedPatient.id,
        chief_complaint: chief,
        priority,
      });
      toast.success(`Session ${res.data.record_id} created`);
      navigate(`/hotline/sessions/${res.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  }

  return (
    <div data-testid="hotline-new-page">
      <PageHeader
        title="New Hotline Session"
        subtitle={step === 1 ? "Step 1 of 2 — identify the patient calling in." : "Step 2 of 2 — capture chief complaint and start the session."}
        actions={
          <Button variant="outline" onClick={() => navigate(-1)} className="border-[#E5E7EB]" data-testid="hotline-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
        }
      />

      {step === 1 && (
        <>
          <FormSection title="Patient Identification" testId="hotline-step-identify" description="Search by MRN, name, mobile, or national ID. If no result, register a hotline-only patient.">
            <FormField label="Search" full>
              <div className="relative">
                <div className="flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-lg px-3 py-2.5">
                  <Search className="w-4 h-4 text-[#94A3B8]" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search patients…"
                    className="bg-transparent outline-none text-sm flex-1 text-[#1F2937] placeholder-[#94A3B8]"
                    data-testid="hotline-patient-search"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRegisterOpen(true)}
                    className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]"
                    data-testid="hotline-register-new"
                  >
                    <UserPlus className="w-4 h-4 mr-1.5" /> Register new
                  </Button>
                </div>
                {results.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-md max-h-80 overflow-y-auto">
                    {results.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => { setSelectedPatient(p); setResults([]); setQuery(`${p.first_name} ${p.last_name}`); }}
                        data-testid={`hotline-result-${p.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#EAF8FA]/50 flex items-center gap-3 border-b border-[#E5E7EB] last:border-b-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
                          {(p.first_name?.[0] || "?") + (p.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#1F2937] truncate">{p.first_name} {p.last_name}</div>
                          <div className="text-xs text-[#6B7280]">
                            {p.mrn} · {p.patient_type === "hotline_only" ? "Hotline-only" : "H@H"} · {p.phone || "no phone"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </FormField>

            {selectedPatient && (
              <FormField label="Selected patient" full>
                <div className="flex items-center gap-3 p-3 border border-[#0B7E8C]/20 rounded-lg bg-[#EAF8FA]/40">
                  <div className="w-10 h-10 rounded-full bg-[#0B7E8C] text-white flex items-center justify-center font-semibold text-sm">
                    {(selectedPatient.first_name?.[0] || "?") + (selectedPatient.last_name?.[0] || "")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#1F2937]">{selectedPatient.first_name} {selectedPatient.last_name}</div>
                    <div className="text-xs text-[#6B7280]">
                      <CopyableId value={selectedPatient.mrn} kind="MRN" /> · DOB {selectedPatient.dob || "—"} · {selectedPatient.phone || "—"} · {selectedPatient.patient_type === "hotline_only" ? "Hotline-only" : "H@H"}
                    </div>
                  </div>
                  <Button onClick={() => setSelectedPatient(null)} variant="outline" size="sm" className="border-[#E5E7EB]">
                    Change
                  </Button>
                </div>
              </FormField>
            )}
          </FormSection>

          <StickyActionBar>
            <Button variant="outline" onClick={() => navigate("/hotline")} className="border-[#E5E7EB]">Cancel</Button>
            <Button
              onClick={() => setStep(2)}
              disabled={!selectedPatient}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
              data-testid="hotline-step-next"
            >
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </StickyActionBar>
        </>
      )}

      {step === 2 && selectedPatient && (
        <>
          <FormSection title="Chief Complaint" testId="hotline-step-complaint">
            <FormField label="Complaint category">
              <Select value={chief.category} onValueChange={(v) => setChief({ ...chief, category: v })}>
                <SelectTrigger data-testid="hotline-cc-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {complaintCategories.map((c) => <SelectItem key={c.id} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Severity">
              <Select value={chief.severity} onValueChange={(v) => setChief({ ...chief, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mild">Mild</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="severe">Severe</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Duration">
              <SearchableSelect value={chief.duration} options={DURATION_SUGGESTIONS.map((s) => ({ value: s, label: s }))} allowCustom placeholder="Select or type duration…" onChange={(v) => setChief({ ...chief, duration: v })} testid="hotline-cc-duration" />
            </FormField>
            <FormField label="Priority">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="hotline-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="stat">STAT</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Description" full>
              <Textarea
                rows={3}
                value={chief.description}
                onChange={(e) => setChief({ ...chief, description: e.target.value })}
                placeholder="Describe the chief complaint in patient's own words…"
                data-testid="hotline-cc-description"
              />
            </FormField>
          </FormSection>

          <StickyActionBar>
            <Button variant="outline" onClick={() => setStep(1)} className="border-[#E5E7EB]">Back</Button>
            <Button onClick={createSession} disabled={creating} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-create-session">
              <PhoneCall className="w-4 h-4 mr-1.5" /> {creating ? "Creating…" : "Create session"}
            </Button>
          </StickyActionBar>
        </>
      )}

      {/* Register patient sheet */}
      {registerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRegisterOpen(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#1F2937] mb-1">Register hotline patient</h3>
            <p className="text-sm text-[#6B7280] mb-4">Quick registration for a new hotline-only patient. They'll be saved into Patient Management.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
              <FormField label="First name" required error={npErrors.first_name}>
                <Input value={newPatient.first_name} onChange={(e) => setNpField("first_name", e.target.value)} data-testid="hotline-reg-first-name" />
              </FormField>
              <FormField label="Middle name">
                <Input value={newPatient.second_name} onChange={(e) => setNpField("second_name", e.target.value)} data-testid="hotline-reg-middle-name" />
              </FormField>
              <FormField label="Last name" required error={npErrors.last_name}>
                <Input value={newPatient.last_name} onChange={(e) => setNpField("last_name", e.target.value)} data-testid="hotline-reg-last-name" />
              </FormField>
              <FormField label="Mobile number" error={npErrors.mobile || npErrors.dial_code} hint="Select the country code first.">
                <div className="flex gap-2">
                  <Select value={newPatient.dial_code} onValueChange={(v) => setNpField("dial_code", v)}>
                    <SelectTrigger className="w-[130px] shrink-0" data-testid="hotline-reg-dial-code"><SelectValue placeholder="Code" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {DIAL_CODES.map((d) => <SelectItem key={d.iso3} value={d.code}>{d.code} · {d.country}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className="flex-1" disabled={!newPatient.dial_code}
                    placeholder={!newPatient.dial_code ? "Select code first" : "Mobile number"}
                    value={newPatient.mobile} onChange={(e) => setNpField("mobile", e.target.value)} data-testid="hotline-reg-mobile" />
                </div>
              </FormField>
              <FormField label="Date of Birth">
                <Input type="date" max={new Date().toISOString().slice(0, 10)} value={newPatient.dob} onChange={(e) => setNpField("dob", e.target.value)} data-testid="hotline-reg-dob" />
              </FormField>
              <FormField label="Gender">
                <Select value={newPatient.gender} onValueChange={(v) => setNpField("gender", v)}>
                  <SelectTrigger data-testid="hotline-reg-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Nationality">
                <Select value={newPatient.nationality} onValueChange={setNationality}>
                  <SelectTrigger data-testid="hotline-reg-nationality"><SelectValue placeholder="Select nationality" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {natOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Identity type" hint={isSaudiNew ? "Locked to National Number for Saudi nationals." : "Select before the identity number."}>
                <Select value={newPatient.identity_type} disabled={isSaudiNew} onValueChange={(v) => setNpField("identity_type", v)}>
                  <SelectTrigger data-testid="hotline-reg-identity-type"><SelectValue placeholder="Select identity type" /></SelectTrigger>
                  <SelectContent>
                    {PATIENT_IDENTITY_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Identity number" full error={npErrors.identity_number}
                hint={newPatient.identity_type === "National Number" ? "Starts with 1, 10 digits." : newPatient.identity_type === "Permanent Resident" ? "Starts with 2, 10 digits." : newPatient.identity_type === "Border Number" ? "10 digits starting with 3 or 4." : newPatient.identity_type === "Passport" ? "5–30, letters + numbers." : "Select identity type first."}>
                <Input disabled={!newPatient.identity_type}
                  placeholder={!newPatient.identity_type ? "Select identity type first" : ""}
                  value={newPatient.identity_number} onChange={(e) => setNpField("identity_number", e.target.value.trim())} data-testid="hotline-reg-identity-number" />
              </FormField>
              <FormField label="Region">
                <Select value={newPatient.region} onValueChange={setRegion}>
                  <SelectTrigger data-testid="hotline-reg-region"><SelectValue placeholder="Select region" /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {SAUDI_REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="City" hint={!newPatient.region ? "Select a region first." : undefined}>
                <Select value={newPatient.city} disabled={!newPatient.region} onValueChange={(v) => setNpField("city", v)}>
                  <SelectTrigger data-testid="hotline-reg-city"><SelectValue placeholder={newPatient.region ? "Select city" : "Select region first"} /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {(SAUDI_CITIES[newPatient.region] || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setRegisterOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
              <Button onClick={registerPatient} disabled={registering} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-reg-save">
                {registering ? "Saving…" : "Register & continue"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
